/**
 * PaiePortal — tuile portail « Paie » (Batch #14.251).
 * Onglets : Employés · Feuilles de temps · Documents reçus · DAS.
 * Le client saisit ; le gestionnaire calcule et publie. Aucun chiffre de retenue n'est affiché ici.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, FolderDown, HardHat, Landmark, Loader2, Users } from "lucide-react";
import { API, portalHeaders } from "./_shared";
import EmployeesSection from "./EmployeesSection";
import TimesheetsSection from "./TimesheetsSection";
import DocumentsSection from "./DocumentsSection";
import DasSection from "./DasSection";
import CcqSection from "./CcqSection";

const TABS = [
  { id: "timesheets", label: "Feuilles de temps", icon: CalendarClock },
  { id: "employees", label: "Employés", icon: Users },
  { id: "documents", label: "Documents reçus", icon: FolderDown },
  { id: "das", label: "DAS", icon: Landmark },
  { id: "ccq", label: "CCQ", icon: HardHat, ccq: true },
];

export default function PaiePortal({ primaryColor }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("timesheets");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/summary`, { headers: portalHeaders() });
      setSummary(r.data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger votre espace Paie");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-sm text-red-700" data-testid="paie-portal-error">{error}</div>;
  if (!summary) return <div className="py-16 text-center" data-testid="paie-portal-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto" /></div>;

  const c = summary.counts;
  return (
    <div className="space-y-4" data-testid="portal-paie">
      <div className="bg-white border rounded-2xl p-4 sm:p-5">
        <h3 className="font-semibold text-gray-900" data-testid="paie-portal-employer">{summary.employer.legal_name || "Ma paie"}</h3>
        <p className="text-xs text-gray-500 mt-0.5">Saisissez vos employés et leurs heures ; votre gestionnaire prépare la paie et vous transmet les documents ici.</p>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-xl p-3 bg-sky-50 text-sky-800" data-testid="paie-portal-kpi-employees">
            <p className="text-[11px] opacity-75">Employés actifs</p>
            <p className="text-lg font-bold mt-0.5 font-mono">{c.employees_active}</p>
          </div>
          <div className="rounded-xl p-3 bg-amber-50 text-amber-800" data-testid="paie-portal-kpi-open">
            <p className="text-[11px] opacity-75">Feuilles en cours</p>
            <p className="text-lg font-bold mt-0.5 font-mono">{c.runs_open}</p>
          </div>
          <div className="rounded-xl p-3 bg-emerald-50 text-emerald-800" data-testid="paie-portal-kpi-docs">
            <p className="text-[11px] opacity-75">Périodes avec documents</p>
            <p className="text-lg font-bold mt-0.5 font-mono">{c.docs}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto scroll-x-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="paie-portal-tabs">
        {TABS.filter((t) => !t.ccq || summary.ccq_enabled).map((t) => {
          const on = t.id === tab;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium border transition whitespace-nowrap flex-shrink-0 ${on ? "text-white shadow" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              style={on ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
              data-testid={`paie-portal-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "timesheets" && <TimesheetsSection summary={summary} primaryColor={primaryColor} onChanged={load} />}
      {tab === "employees" && <EmployeesSection summary={summary} primaryColor={primaryColor} onChanged={load} />}
      {tab === "documents" && <DocumentsSection primaryColor={primaryColor} />}
      {tab === "das" && <DasSection primaryColor={primaryColor} />}
      {tab === "ccq" && summary.ccq_enabled && <CcqSection primaryColor={primaryColor} />}
    </div>
  );
}
